import { pb, escapeFilterValue } from '../lib/pocketbase'
import { cascadeDelete, containsFilter, entityFilter } from '../utils/cascadeDeleteHelper'
import type {
  Contact,
  ContactRoleType,
  ContactPreferredMethod,
  ContactRelationshipHealth,
  ContactPriority,
} from '../types'
import { ensureUserAgency } from '../utils/ensureAgency'

export type ContactPayload = {
  first_name: string
  last_name: string
  avatar_url?: string | null
  title?: string | null
  role_type?: ContactRoleType | null
  department?: string | null
  email?: string | null
  phone?: string | null
  mobile?: string | null
  preferred_contact_method?: ContactPreferredMethod | null
  linkedin_url?: string | null
  twitter_handle?: string | null
  organization_id?: string | null
  relationship_health?: ContactRelationshipHealth | null
  last_contact_date?: string | null
  next_follow_up?: string | null
  interaction_count?: number | null
  priority?: ContactPriority | null
  is_primary_contact?: boolean | null
  is_decision_maker?: boolean | null
  tags?: string[] | null
  notes?: string | null
  agency_id?: string
  user_id?: string | null
}

function mapContact(record: Record<string, any>): Contact {
  return {
    id: record.id,
    first_name: record.first_name ?? '',
    last_name: record.last_name ?? '',
    avatar_url: record.avatar_url ?? null,
    title: record.title ?? null,
    role_type: record.role_type ?? null,
    department: record.department ?? null,
    email: record.email ?? null,
    phone: record.phone ?? null,
    mobile: record.mobile ?? null,
    preferred_contact_method: record.preferred_contact_method ?? null,
    linkedin_url: record.linkedin_url ?? null,
    twitter_handle: record.twitter_handle ?? null,
    organization_id: record.organization_id ?? null,
    relationship_health: record.relationship_health ?? null,
    last_contact_date: record.last_contact_date ?? null,
    next_follow_up: record.next_follow_up ?? null,
    interaction_count: record.interaction_count ?? null,
    priority: record.priority ?? null,
    is_primary_contact: record.is_primary_contact ?? null,
    is_decision_maker: record.is_decision_maker ?? null,
    tags: Array.isArray(record.tags) ? record.tags : null,
    notes: record.notes ?? null,
    agency_id: record.agency_id ?? '',
    user_id: record.user_id ?? null,
    created: record.created ?? '',
    updated: record.updated ?? '',
  }
}

export async function listContacts() {
  
  // Get current user's agency for filtering
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  // If no agency, return empty - user needs to complete onboarding
  if (!agencyId) {
    return []
  }

  const data = await pb.collection('contacts').getFullList<Record<string, any>>({
    filter: `agency_id = "${escapeFilterValue(agencyId)}"`,
    sort: '+last_name,+first_name',
    batch: 500,
  })
  return data.map(mapContact)
}

export async function getContact(contactId: string) {
  const record = await pb.collection('contacts').getOne<Record<string, any>>(contactId, {
    expand: 'organization_id',
  })
  return mapContact(record)
}

export async function createContact(payload: ContactPayload, userId?: string) {
  const user = pb.authStore.model
  const agencyId = payload.agency_id || await ensureUserAgency()

  const createPayload = {
    ...payload,
    agency_id: agencyId,
    // Always capture the creator
    user_id: userId || payload.user_id || user?.id || '',
  }

  const record = await pb.collection('contacts').create(createPayload)
  return mapContact(record)
}

export async function updateContact(
  contactId: string,
  payload: Partial<ContactPayload>,
) {
  const record = await pb.collection('contacts').update(contactId, payload)
  return mapContact(record)
}

/**
 * Delete a contact and what hangs off it: the birthday rows the schedule
 * job wrote for them, and tasks and notes that point at them. Until Sep
 * 2026 this deleted the contact alone, so a birthday kept appearing for a
 * person no longer in the book.
 */
export async function deleteContact(contactId: string) {
  await pb.collection('contacts').delete(contactId)
  await cascadeDelete([
    { collection: 'calendar_events', filter: containsFilter('external_id', `birthday:contact:${contactId}:`) },
    { collection: 'tasks',           filter: entityFilter('entity_type', 'contact', 'entity_id', contactId) },
    { collection: 'notes',           filter: entityFilter('related_entity_type', 'contact', 'related_entity_id', contactId) },
  ])
}






